import { ChevronLeft, ChevronRight, Maximize2, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type TouchEvent } from "react";
import type { ProductImage } from "../../types/content";

function ratioValue(image: ProductImage) {
  if (image.aspectRatio === "custom" && image.customWidth && image.customHeight) {
    return `${image.customWidth} / ${image.customHeight}`;
  }
  return image.aspectRatio.replace(":", " / ");
}

export function ProductGallery({
  images,
  productName,
  compact = false,
}: {
  images: ProductImage[];
  productName: string;
  compact?: boolean;
}) {
  const activeImages = images
    .filter((image) => image.isActive)
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .slice(0, 3);
  const [selected, setSelected] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [isInteracting, setIsInteracting] = useState(false);
  const [motion, setMotion] = useState<{
    direction: "next" | "previous" | null;
    id: number;
  }>({ direction: null, id: 0 });
  const touchStartX = useRef<number | null>(null);
  const imageCount = activeImages.length;

  const move = useCallback((direction: number) => {
    if (imageCount < 2) return;
    setMotion((current) => ({
      direction: direction > 0 ? "next" : "previous",
      id: current.id + 1,
    }));
    setSelected((current) => (current + direction + imageCount) % imageCount);
  }, [imageCount]);

  useEffect(() => {
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (
      imageCount < 2 ||
      reducedMotion.matches ||
      isInteracting ||
      lightbox
    ) {
      return;
    }
    const timer = window.setInterval(() => {
      if (!document.hidden) move(1);
    }, 3_500);
    return () => window.clearInterval(timer);
  }, [imageCount, isInteracting, lightbox, move]);

  if (!imageCount) {
    return (
      <div className="gallery-empty">
        <span>{productName.slice(0, 1)}</span>
        <p>Product preview coming soon</p>
      </div>
    );
  }

  const activeIndex = selected % imageCount;
  const previousIndex = (activeIndex - 1 + imageCount) % imageCount;
  const nextIndex = (activeIndex + 1) % imageCount;
  const mobileSlides = imageCount > 1
    ? [
        { image: activeImages[previousIndex], position: "previous" as const },
        { image: activeImages[activeIndex], position: "active" as const },
        { image: activeImages[nextIndex], position: "next" as const },
      ]
    : [{ image: activeImages[activeIndex], position: "active" as const }];

  const selectImage = (index: number) => {
    if (index === activeIndex) return;
    const forward = (index - activeIndex + imageCount) % imageCount;
    const backward = (activeIndex - index + imageCount) % imageCount;
    move(forward <= backward ? 1 : -1);
  };

  const onTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    touchStartX.current = event.touches[0]?.clientX ?? null;
    setIsInteracting(true);
  };

  const onTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    const start = touchStartX.current;
    const end = event.changedTouches[0]?.clientX;
    touchStartX.current = null;
    setIsInteracting(false);
    if (start == null || end == null) return;
    const distance = end - start;
    if (Math.abs(distance) >= 42) move(distance < 0 ? 1 : -1);
  };

  return (
    <>
      <div className={`product-gallery ${compact ? "is-compact" : ""}`}>
        <div
          className="gallery-mobile"
          role="region"
          aria-roledescription="carousel"
          aria-label={`${productName} image gallery`}
          onMouseEnter={() => setIsInteracting(true)}
          onMouseLeave={() => setIsInteracting(false)}
          onFocusCapture={() => setIsInteracting(true)}
          onBlurCapture={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) setIsInteracting(false);
          }}
        >
          <div
            className="gallery-mobile-viewport"
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <div
              key={motion.id}
              className={`gallery-mobile-track ${
                motion.direction ? `is-moving-${motion.direction}` : ""
              }`}
              aria-live="off"
            >
              {mobileSlides.map(({ image, position }) => (
                <div
                  key={`${position}-${image.id}`}
                  className={`gallery-mobile-frame is-${position}`}
                >
                  <img
                    src={image.imageUrl}
                    alt={position === "active" ? image.altText : ""}
                    width={image.customWidth}
                    height={image.customHeight}
                    loading={
                      compact
                        ? "lazy"
                        : position === "active" && activeIndex === 0
                          ? "eager"
                          : "lazy"
                    }
                    decoding="async"
                    fetchPriority={
                      !compact && position === "active" && activeIndex === 0
                        ? "high"
                        : "auto"
                    }
                    onClick={position === "active" ? () => setLightbox(true) : undefined}
                    style={position === "active" ? { cursor: "zoom-in" } : undefined}
                  />
                  {position === "active" ? (
                    <button
                      className="gallery-expand"
                      onClick={() => setLightbox(true)}
                      aria-label="Open image preview"
                    >
                      <Maximize2 size={17} />
                    </button>
                  ) : (
                    <button
                      className="gallery-slide-jump"
                      onClick={() => move(position === "next" ? 1 : -1)}
                      aria-label={`${position === "next" ? "Next" : "Previous"} image`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
          {imageCount > 1 && (
            <div className="gallery-controls">
              <button onClick={() => move(-1)} aria-label="Previous image">
                <ChevronLeft size={18} />
              </button>
              <div className="gallery-dots">
                {activeImages.map((image, index) => (
                  <button
                    key={image.id}
                    className={activeIndex === index ? "is-active" : ""}
                    onClick={() => selectImage(index)}
                    aria-label={`Show image ${index + 1}`}
                    aria-current={activeIndex === index ? "true" : undefined}
                  />
                ))}
              </div>
              <button onClick={() => move(1)} aria-label="Next image">
                <ChevronRight size={18} />
              </button>
            </div>
          )}
        </div>
        <div className={`gallery-desktop gallery-count-${activeImages.length}`}>
          {activeImages.map((image, index) => (
            <button
              key={image.id}
              className={`gallery-tile gallery-tile-${index + 1}`}
              style={{ aspectRatio: ratioValue(image), ["--tile-ratio" as string]: ratioValue(image) }}
              onClick={() => {
                setSelected(index);
                setLightbox(true);
              }}
              aria-label={`Preview ${image.altText}`}
            >
              <img
                src={image.imageUrl}
                alt={image.altText}
                width={image.customWidth}
                height={image.customHeight}
                loading={!compact && index === 0 ? "eager" : "lazy"}
                decoding="async"
                fetchPriority={!compact && index === 0 ? "high" : "auto"}
              />
              <span className="gallery-tile-action">
                View <Maximize2 size={14} />
              </span>
            </button>
          ))}
        </div>
      </div>
      {lightbox && (
        <div className="lightbox" role="dialog" aria-modal="true" aria-label={`${productName} image preview`}>
          <button className="lightbox-close" onClick={() => setLightbox(false)} aria-label="Close preview">
            <X size={22} />
          </button>
          {imageCount > 1 && (
            <button className="lightbox-nav is-left" onClick={() => move(-1)} aria-label="Previous image">
              <ChevronLeft size={25} />
            </button>
          )}
          <img
            src={activeImages[activeIndex].imageUrl}
            alt={activeImages[activeIndex].altText}
            width={activeImages[activeIndex].customWidth}
            height={activeImages[activeIndex].customHeight}
            decoding="async"
          />
          {imageCount > 1 && (
            <button className="lightbox-nav is-right" onClick={() => move(1)} aria-label="Next image">
              <ChevronRight size={25} />
            </button>
          )}
          <p>
            {activeIndex + 1} / {imageCount} · {activeImages[activeIndex].altText}
          </p>
        </div>
      )}
    </>
  );
}
